import { useEffect, useRef } from "react";
import { notifications } from "@mantine/notifications";
import { useFileManager } from "@/components/core/FileManager/FileManagerContext/FileManagerContext.ts";
import { useFileManagerUI } from "@/components/core/FileManager/FileManagerContext/FileManagerUIContext.ts";

function isInputTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable;
}

export function FileManagerKeyboardShortcuts() {
  const { copyItems, currentFolderId } = useFileManager();
  const { selectedItems, clipboardItems, copyToClipboard, clearClipboard } = useFileManagerUI();

  const selectedItemsRef = useRef(selectedItems);
  selectedItemsRef.current = selectedItems;

  const clipboardItemsRef = useRef(clipboardItems);
  clipboardItemsRef.current = clipboardItems;

  const currentFolderIdRef = useRef(currentFolderId);
  currentFolderIdRef.current = currentFolderId;

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const isCmd = e.metaKey || e.ctrlKey;
      if (!isCmd) return;
      if (isInputTarget(e.target)) return;

      if (e.key === "c") {
        const items = selectedItemsRef.current;
        if (items.length === 0) return;
        e.preventDefault();
        copyToClipboard(items);
        notifications.show({
          message: `${items.length} item${items.length !== 1 ? "s" : ""} ready to paste`,
          color: "blue",
          autoClose: 2000,
        });
        return;
      }

      if (e.key === "v") {
        const clipboard = clipboardItemsRef.current;
        if (clipboard.length === 0) return;
        const folderId = currentFolderIdRef.current;
        if (!folderId) return;
        e.preventDefault();
        copyItems(
          {
            items: clipboard.map((item) => ({ id: item.id, name: item.name, type: item.type })),
            destination: { id: folderId, name: null },
          },
          {
            onSuccess: clearClipboard,
            onError: () => {
              notifications.show({
                title: "Paste failed",
                message: "Could not paste items. Please try again.",
                color: "red",
                autoClose: 4000,
              });
            },
          },
        );
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [copyItems, copyToClipboard, clearClipboard]);

  return null;
}
